import { Toaster, toast } from "react-hot-toast";
import { Invoices } from "../../../service";
import CircularLoader from "../../Ui/Loader";
import useApiHandler from "../../hooks/ApiHandler";
import { UserContext } from "../../context/UserContext";
import TableScreen from "../common/TableScreen/TableScreen";
import { formatDate, formatTime, getHeaders, humanizeEnum, formatCurrency, isWarrantyExpired } from "../../utils/utils";
import React, {useState, useContext, useEffect} from "react";
import { useNavigate } from "react-router-dom";
import { searchbarVisibilty } from "../../constant/Home/Dashboard";
import { useCurrentSelectedData } from "../../context/CurrentSelectedDataContext";
import { Button, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import GenericModal from "../../Ui/Modal";
import ButtonComponent from "../../Ui/Button";
import PlusIcon from "assets/icons/Plus.svg";
import { useStatisticsData } from "../../context/StatisticsData";

const OpenInvoices = ({}) => {
  const { userData } = useContext(UserContext);
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const { callApi, loading } = useApiHandler();
  const navigate = useNavigate();
  const { setCurrentSelectedData } = useCurrentSelectedData();
  const [currentPage, setCurrentPage] = useState(1);
  const [resultsPerPage, setResultsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [invoices, setInvoices] = useState([]);
  const [showWarrantyExpired, setShowWarrantyExpired] = useState(false);
    const [payingId, setPayingId] = useState(null);
    const [viewingId, setViewingId] = useState(null);
    const [payNowConfirmOpen, setPayNowConfirmOpen] = useState(false);
    const [payNowRow, setPayNowRow] = useState(null);

    const { refreshStatistics } = useStatisticsData();

  useEffect(() => {
    console.log("invoices", invoices);
  }, [invoices]);

  const handleWarrantyClick = (row) => {
    if (isWarrantyExpired(row)) {
      setShowWarrantyExpired(true);
      return;
    }
    const jobId = row?.jobSnapshot?.jobId || row?.jobId || row?.id;
    navigate(`/warranty-service/${jobId}`.replace(/\/$/, ""), {
      state: { invoice: row },
    });
  };

  const handleViewInvoice = async (row) => {
    const id = row?.id;
    if (!id) return toast.error("Missing invoice id");

    const url =
      row?.stripe_invoice_url ||
      row?.stripeInvoiceUrl ||
      row?.stripeInvoiceUrl?.url ||
      row?.stripeInvoiceUrl?.href ||
      null;

    if (!url) return toast.error("Invoice link isn't available yet.");

    setViewingId(id);
    try {
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.log("Error opening invoice URL:", e);
      toast.error("Couldn't open invoice link.");
    } finally {
      setViewingId(null);
    }
  };

    const handlePayNow = async (row) => {
    const id = row?.id;
    if (!id) return toast.error("Missing invoice id");
    setPayingId(id);
    try {
      await callApi(() => Invoices.payNow(id), {
        onSuccess: (response) => {
          const data = response?.data || response;
          const msg =
            data?.message || "Payment scheduled to run immediately.";
          toast.success(msg);
          // Refresh list to reflect any status changes
          getOpenInvoices();
          // Keep dashboard card counts in sync (Open Invoices should decrement)
          refreshStatistics();
        },
        finallyCallback: () => setPayingId(null),
      });
    } catch (e) {
      setPayingId(null);
    }
    };

  const openPayNowConfirm = (row) => {
    setPayNowRow(row);
    setPayNowConfirmOpen(true);
  };

  const closePayNowConfirm = () => {
    setPayNowConfirmOpen(false);
    setPayNowRow(null);
  };

  const confirmPayNow = async () => {
    const row = payNowRow;
    closePayNowConfirm();
    if (!row) return;
    await handlePayNow(row);
  };

  const renderCell = (row, header, colIdx, options = {}) => {
    console.log(row, "row");
    const { title, setModalState } = options;

    switch (header) {
      case "DATE ISSUED":
        return formatDate(row?.createdAt);
      case "INVOICE ID":
        return row?.id;
      case "PROPERTY ADDRESS":
        return row?.jobSnapshot?.propertyAddress;
      case "AMOUNT": {
        const total = row?.totalBilled ?? row?.total_billed ?? row?.subtotal ?? row?.amount;
        const subtotal = row?.subtotal;
        const stripeFee = row?.stripeFeeAmount ?? row?.stripe_fee_amount;
        const tax = row?.taxAmount ?? row?.tax_amount;

        const isEstimate = stripeFee !== undefined || tax !== undefined;

        const tooltipContent = (
          <div className="p-1">
            <div className="flex justify-between gap-4">
              <span>Subtotal:</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
            {stripeFee && (
              <div className="flex justify-between gap-4">
                <span>Processing Fee:</span>
                <span>{formatCurrency(stripeFee)}</span>
              </div>
            )}
            {tax && (
              <div className="flex justify-between gap-4">
                <span>Tax:</span>
                <span>{formatCurrency(tax)}</span>
              </div>
            )}
            <div className="flex justify-between gap-4 font-bold border-t border-gray-400 mt-1 pt-1">
              <span>Total:</span>
              <span>{formatCurrency(total)}</span>
            </div>
          </div>
        );

        return (
          <div className="flex flex-col">
            <div className="flex items-center gap-1">
              <span className="font-medium text-[#242424]">
                {formatCurrency(total)}
              </span>
              {isEstimate && (
                <Tooltip title={tooltipContent} arrow>
                  <InfoOutlinedIcon style={{ fontSize: 16, color: "#595959", cursor: "pointer" }} />
                </Tooltip>
              )}
            </div>
          </div>
        );
      }
      case "TIME ISSUED": {
        // Show human-readable local time only; date is already shown in DATE ISSUED
        const source = row?.createdAt || row?.created_at || row?.timeIssued;
        return formatTime(source);
      }
      case "LOCATION":
        return row.location;
      case "ESTIMATED RANGE":
        return row.estimatedRange;
      case "VENDOR":
        return row.vendorName || "";
      case "WARRANTY SERVICE":
        return (
          <Button
            variant="outlined"
            size="small"
            style={{
              borderRadius: 8,
              textTransform: "none",
              border: "1px solid #ECECEC",
              color: "#242424",
              whiteSpace: "nowrap",
            }}
            onClick={() => handleWarrantyClick(row)}
          >
            Warranty Service
          </Button>
        );
      case "JOB DESCRIPTION":
        return row.jobSnapshot?.description || "No description provided";
      case "JOB TITLE": {
        const js = row?.jobSnapshot || {};
        // Prefer any display-friendly labels first
        const displayTitle =
          js?.jobTitleDisplay ??
          js?.job_title_display ??
          js?.subIssueTypeDisplay ??
          js?.sub_issue_type_display ??
          js?.issueTypeDisplay ??
          js?.issue_type_display ??
          null;

        if (displayTitle) return displayTitle;

        // Fallback to humanizing enum-like fields
        const rawTitle =
          js?.jobTitle ??
          js?.job_title ??
          js?.subIssueType ??
          js?.sub_issue_type ??
          js?.issueType ??
          js?.issue_type ??
          null;

        return rawTitle ? humanizeEnum(String(rawTitle)) : "N/A";
      }
      case "DATE PAID":
        return row.datePaid || "N/A";
      case "VIEW DETAILS":
        // Temporarily hidden on Open Invoices for all user types
        // return (
        //   <Button
        //     variant="outlined"
        //     size="small"
        //     style={{
        //       borderRadius: 8,
        //       textTransform: "none",
        //       border: "1px solid #ECECEC",
        //       color: "#242424",
        //     }}
        //     onClick={() => {
        //       setModalState("jobDetails");
        //       setCurrentSelectedData(row);
        //     }}
        //   >
        //     View
        //   </Button>
        // );
        return null;
      case "VIEW DETAILS TO ACCEPT":
        // Temporarily hidden on Open Invoices for all user types
        // return (
        //   <Button
        //     variant="outlined"
        //     size="small"
        //     style={{
        //       borderRadius: 8,
        //       textTransform: "none",
        //       border: "1px solid #ECECEC",
        //       color: "#242424",
        //     }}
        //     onClick={() => {
        //       setModalState("jobDetails");
        //     }}
        //   >
        //     View
        //   </Button>
        // );
        return null;
      case "APPROVE":
        return (
          <Button
            variant="contained"
            size="small"
            style={{
              borderRadius: 8,
              textTransform: "none",
              backgroundColor: "#F2FBF4",
              color: "#3CAB5C",
              border: "1px solid #3CAB5C",
            }}
          >
            Approve
          </Button>
        );
      case "PAY NOW":
        return (
          <Button
            variant="contained"
            size="small"
            style={{
              borderRadius: 100,
              textTransform: "none",
              backgroundColor: "#F2FBF4",
              color: "#3CAB5C",
              border: "1px solid #3CAB5C",
              minWidth: "77px",
              textWrap: "nowrap",
            }}
            disabled={payingId === row?.id}
            onClick={() => openPayNowConfirm(row)}
          >
            {payingId === row?.id ? "Processing..." : "Pay Now"}
          </Button>
        );
      case "VIEW":
        return (
          <Button
            variant="outlined"
            size="small"
            style={{
              borderRadius: 8,
              textTransform: "none",
              border: "1px solid #ECECEC",
              color: "#242424",
            }}
            disabled={viewingId === row?.id}
            href={row?.stripe_invoice_url || row?.stripeInvoiceUrl || row?.stripeInvoiceUrl?.url || row?.stripeInvoiceUrl?.href || undefined} target="_blank" rel="noopener noreferrer" component="a"
          >
            View
          </Button>
        );
      default:
        return row[header.toLowerCase()] || "N/A";
    }
  };

  const getOpenInvoices = async (opts = {}) => {
    const { silent } = opts;
    await callApi(() => Invoices.getOpenInvoices(currentPage, resultsPerPage), {
      silent,
      onSuccess: (response) => {
        if (response.data) {
          setInvoices(response.data?.results || response.data);
          setTotalCount(response.data?.count || response.data?.length || 0);
        }
      },
      onError: () => {
        // Intentionally silent; UI already handles empty state and ApiHandler may toast/log.
      },
    });
  };

  // Handle results per page change
  const handleResultsPerPageChange = (newResultsPerPage) => {
    setResultsPerPage(newResultsPerPage);
    setCurrentPage(1); // Reset to first page when changing results per page
  };

  React.useEffect(() => {
    getOpenInvoices();
  }, [currentPage, resultsPerPage]);

  // Refresh open invoices list during dashboard polling while this tab is mounted
  React.useEffect(() => {
    const onPoll = () => {
      getOpenInvoices({ silent: true }).catch(() => {});
    };
    window.addEventListener("dashboard:poll", onPoll);
    return () => window.removeEventListener("dashboard:poll", onPoll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, resultsPerPage]);

  const filteredRows = React.useMemo(() => {
    if (!invoiceSearch) return invoices;
    const searchLower = invoiceSearch.toLowerCase();

    return invoices.filter((row) => {
      return (
        String(row.id || "")
          .toLowerCase()
          .includes(searchLower) ||
        String(row.jobSnapshot?.jobTitle || "")
          .toLowerCase()
          .includes(searchLower) ||
        String(row.jobSnapshot?.propertyAddress || "")
          .toLowerCase()
          .includes(searchLower)
      );
    });
  }, [invoiceSearch, invoices]);

  return (
    <div className=" max-w-[1440px] m-auto  ">
      <Toaster />
      {loading ? (
        <CircularLoader minHeight="400px" />
      ) : invoices.length ? (
        <TableScreen
          title="Open Invoices"
          description="View, download, and pay all open invoices for your jobs."
          placeholder="Search by invoice id, property name, job title"
          headers={getHeaders(
            userData?.accountType || "default",
            "openInvoices"
          )}
          rows={filteredRows}
          onSearch={setInvoiceSearch}
          search={
            searchbarVisibilty[userData?.accountType || "default"]?.openInvoices
          }
          renderCell={renderCell}
          // Pagination props
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          resultsPerPage={resultsPerPage}
          onResultsPerPageChange={handleResultsPerPageChange}
          totalCount={totalCount}
          useExternalPagination={true}
        />
      ) : (
        <div className="text-center py-8 text-[#595959]">
          <p className="text-[16px] font-[500]">
            No open invoices at the moment.
          </p>
          <p className="text-[14px] mt-2">
            All invoices that require payment will appear here.
          </p>
        </div>
      )}

      {/* Warranty Expired Modal */}
      <GenericModal
        open={showWarrantyExpired}
        onClose={() => setShowWarrantyExpired(false)}
        title=" "
        logoDetail="Warranty Expired"
        children={
          <div className="md:p-6 p-4">
            <p className="text-center text-[#595959] text-[12px] max-w-[720px] m-auto">
              The warranty for this job has expired. If you are experiencing issues, please submit a new job request
              and we will help get your problem resolved.
            </p>
          </div>
        }
        footer={
          <div className="w-full flex justify-end">
            <ButtonComponent
              bgColor="#3CAB5C"
              color="#FFFFFF"
              onClick={() => {
                setShowWarrantyExpired(false);
                navigate("/post-job");
              }}
            >
              <span className="flex items-center gap-2">
                <img src={PlusIcon} alt="plus" className="w-[14px] h-[14px]" />
                Post a job
              </span>
            </ButtonComponent>
          </div>
        }
        maxWidth="756px"
      />

      {/* Pay Now Confirmation Modal */}
      <GenericModal
        open={payNowConfirmOpen}
        onClose={closePayNowConfirm}
        title={null}
        logoDetail="Confirm Payment"
        children={
          <div className="md:p-6 p-4">
            <p className="text-center text-[#595959] text-[16px] max-w-[720px] m-auto">
              Are you sure you want to pay this invoice now?
            </p>
          </div>
        }
        footer={
          <div className="w-full flex justify-end gap-2">
            <Button
              variant="outlined"
              onClick={closePayNowConfirm}
              disabled={payingId === payNowRow?.id}
              style={{
                borderRadius: 8,
                textTransform: "none",
                border: "1px solid #ECECEC",
                color: "#242424",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={confirmPayNow}
              disabled={!payNowRow || payingId === payNowRow?.id}
              style={{
                borderRadius: 8,
                textTransform: "none",
                backgroundColor: "#3CAB5C",
                color: "#FFFFFF",
              }}
            >
              {payingId === payNowRow?.id ? "Processing..." : "Confirm"}
            </Button>
          </div>
        }
        maxWidth="756px"
      />
    </div>
  );
};

export default OpenInvoices;
